import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useMatches } from "@/components/MatchContext";
import { Menu, X, User, LogOut, ChevronDown, Bookmark, Handshake } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { incomingCount } = useMatches();
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { href: "/members", label: "Directory" },
    { href: "/businesses", label: "Businesses" },
    { href: "/events", label: "Events" },
    { href: "/blog", label: "Resources" },
    { href: "/community", label: "Community" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b-2 border-[#0A0A0A]" data-testid="navbar">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center mr-6 lg:mr-12" data-testid="navbar-logo">
            <span className="font-logo text-base md:text-lg uppercase whitespace-nowrap" style={{letterSpacing: '-0.03em', wordSpacing: '-0.05em'}}>
              THE <span className="text-[#FF2D87]">CREATIVE</span> ENSEMBLE
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link ${isActive(link.href) ? "active" : ""}`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="relative flex items-center gap-2 px-4 py-2 border-2 border-[#0A0A0A] hover:bg-[#FFC2E8] transition-colors" data-testid="user-menu-trigger">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-[#0A0A0A]" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#FF2D87] flex items-center justify-center text-white text-sm font-bold">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium text-sm">{user.name?.split(" ")[0]}</span>
                  <ChevronDown className="w-4 h-4" />
                  {incomingCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 bg-[#5170FF] text-white rounded-full text-[10px] min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold border-2 border-white"
                      data-testid="navbar-match-badge"
                    >
                      {incomingCount}
                    </span>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 border-2 border-[#0A0A0A] rounded-none shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
                  <DropdownMenuItem asChild>
                    <Link to="/home" className="flex items-center gap-2 cursor-pointer" data-testid="menu-dashboard">
                      <User className="w-4 h-4" />
                      Dashboard
                      {incomingCount > 0 && (
                        <span className="ml-auto inline-flex items-center gap-1 bg-[#5170FF] text-white rounded-full text-[10px] px-2 py-0.5 font-bold">
                          <Handshake className="w-3 h-3" /> {incomingCount}
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="menu-profile">
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {(user.is_admin || user.is_founder || user.is_founding_member || user.membership_tier !== "free") && (
                    <DropdownMenuItem asChild>
                      <Link to="/businesses" className="flex items-center gap-2 cursor-pointer" data-testid="menu-business-profile">
                        <User className="w-4 h-4" />
                        Business Profile
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.is_admin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-[#FF2D87]" data-testid="menu-admin">
                        <User className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 cursor-pointer text-[#FF2D87]" data-testid="menu-logout">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" className="btn-secondary text-xs py-2 px-4" data-testid="nav-login">
                  Login
                </Link>
                <Link to="/login" className="btn-primary text-xs py-2 px-4" data-testid="nav-join">
                  Join Us
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <div className="container-main py-6">
            <div className="flex justify-between items-center mb-8">
              <span className="font-logo text-xl uppercase" style={{letterSpacing: '-0.03em', wordSpacing: '-0.05em'}}>
                THE <span className="text-[#FF2D87]">CREATIVE</span> ENSEMBLE
              </span>
              <button onClick={() => setMobileMenuOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="mobile-menu-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-6 pt-6 border-t-2 border-[#E5E5E5]">
                {user ? (
                  <>
                    <Link
                      to="/home"
                      className="mobile-menu-link"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/profile"
                      className="mobile-menu-link"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    {(user.is_admin || user.is_founder || user.is_founding_member || user.membership_tier !== "free") && (
                      <Link
                        to="/businesses"
                        className="mobile-menu-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Business Profile
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="mobile-menu-link text-left text-[#FF2D87] w-full"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-4">
                    <Link
                      to="/login"
                      className="btn-secondary text-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/login"
                      className="btn-primary text-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Join Us
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
